import React, { useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider, useQuery } from 'react-query';
import Navbar from './Navbar';
import Hero from './Hero';
import PlatformOverview from './PlatformOverview';
import DetailedAnalytics from './DetailedAnalytics';
import AIChatPrompt from './AIChatPrompt';
import FloatingAI from './FloatingAI';
import Footer from './Footer';
import ErrorBoundary from './ErrorBoundary';
import LoadingSpinner from './LoadingSpinner';
import FacebookTiles from './FacebookTiles';
import FacebookAIAnalysis from './FacebookAIAnalysis';
import EventTiles from './EventTiles';
import { analyticsAPI } from '../services/api';
import configService from '../services/configService';
import { facebookService } from '../services/facebookService';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const DashboardContent = ({ user, onLogout }) => {
  const [timeRange, setTimeRange] = useState('7d');
  const [isAIChatPromptOpen, setIsAIChatPromptOpen] = useState(false);
  const [hasAPIKeys, setHasAPIKeys] = useState(false);
  const [isCheckingConfig, setIsCheckingConfig] = useState(true);
  const [facebookData, setFacebookData] = useState(null);
  const [selectedTile, setSelectedTile] = useState(null);
  const [isAIAnalysisOpen, setIsAIAnalysisOpen] = useState(false);

  // Check if API keys are configured
  useEffect(() => {
    const checkAPIKeys = async () => {
      try {
        console.log('Checking API configuration...');
        const response = await fetch('https://cgb-backend.carl-g-bisaillon.workers.dev/api/config/status');
        if (response.ok) {
          const data = await response.json();
          console.log('API configuration response:', data);
          const configuredPlatforms = Object.keys(data.config).filter(platform => 
            data.config[platform].configured
          );
          console.log('Configured platforms:', configuredPlatforms);
        const hasKeys = configuredPlatforms.length > 0;
        setHasAPIKeys(hasKeys);
      } else {
        console.error('Failed to fetch API configuration:', response.status);
        setHasAPIKeys(false);
      }
    } catch (error) {
      console.error('Failed to check API configuration:', error);
      setHasAPIKeys(false);
    } finally {
      setIsCheckingConfig(false);
    }
    };

    checkAPIKeys();
    loadFacebookData();
  }, []);

  // Load Facebook data only if Facebook is configured
  const loadFacebookData = async () => {
    try {
      // Check if Facebook is configured first
      const response = await fetch('https://cgb-backend.carl-g-bisaillon.workers.dev/api/config/status');
      if (response.ok) {
        const configData = await response.json();
        if (configData.config?.facebook?.configured) {
          const data = await facebookService.getComprehensiveData();
          setFacebookData(data);
        } else {
          console.log('Facebook not configured, skipping data load');
          setFacebookData(null);
        }
      }
    } catch (error) {
      console.error('Error loading Facebook data:', error);
      setFacebookData(null);
    }
  };

  // Handle tile click for AI analysis
  const handleTileClick = (tile) => {
    setSelectedTile(tile);
    setIsAIAnalysisOpen(true);
  };

  // Fetch data based on configuration
  const { data: apiData, isLoading, error, refetch } = useQuery(
    ['analytics', timeRange],
    () => analyticsAPI.getAllData(timeRange),
    {
      enabled: hasAPIKeys,
      onError: (error) => {
        console.error('API Error:', error);
      }
    }
  );

  // Generate mock data as fallback
  const generateMockData = () => ({
    facebook: {
      followers: 15420,
      reach: 8920,
      engagement: 4.2,
      pageLikes: 15420,
      postReach: 8920,
      postEngagement: 1250,
      shares: 340,
      trend: '+12%'
    },
    instagram: {
      followers: 12850,
      reach: 15600,
      engagement: 6.8,
      impressions: 15600,
      likes: 2100,
      comments: 180,
      trend: '+8%'
    },
    google: {
      sessions: 45600,
      pageViews: 67800,
      bounceRate: 42.5,
      avgSessionDuration: 2.3,
      trend: '+15%'
    },
    cloudflare: {
      requests: 125000,
      bandwidth: 2.4,
      threats: 45,
      uptime: 99.9,
      trend: '+5%'
    }
  });

  // Use real data if available, otherwise use mock data
  const data = apiData ? apiData : generateMockData();

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const calculateHeroStats = () => {
    if (!data) return { totalFollowers: 0, totalViews: 0, engagementRate: 0 };
    
    const totalFollowers = (data.facebook?.followers || 0) + (data.instagram?.followers || 0);
    const totalViews = (data.google?.pageViews || 0) + (data.instagram?.impressions || 0);
    const avgEngagement = ((data.facebook?.engagement || 0) + (data.instagram?.engagement || 0)) / 2;
    
    return {
      totalFollowers: formatNumber(totalFollowers),
      totalViews: formatNumber(totalViews),
      engagementRate: avgEngagement.toFixed(1) + '%'
    };
  };

  const handleTimeRangeChange = (newTimeRange) => {
    setTimeRange(newTimeRange);
  };


  if (isCheckingConfig || isLoading) {
    return <LoadingSpinner message="Chargement des données d'analyse..." />;
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Erreur de Chargement des Données</h2>
        <p>{error.message}</p>
        <button onClick={() => window.location.reload()}>
          Recharger la Page
        </button>
        <button onClick={() => refetch()}>
          Réessayer
        </button>
      </div>
    );
  }

  const heroStats = calculateHeroStats();

  return (
    <div className="dashboard-app">
        <Navbar 
          user={user} 
          onLogout={onLogout}
          hasAPIKeys={hasAPIKeys}
          onOpenAIChatPrompt={() => setIsAIChatPromptOpen(true)}
        />
      <Hero stats={heroStats} />
      <main className="dashboard">
        <div className="dashboard-container">
          <PlatformOverview 
            data={data} 
            formatNumber={formatNumber}
            onTimeRangeChange={handleTimeRangeChange}
            timeRange={timeRange}
            errors={apiData?.errors}
            hasAPIKeys={hasAPIKeys}
          />
          
                  {/* Facebook Tiles Section - Only show if we have real data with actual values */}
                  {hasAPIKeys && facebookData && facebookData.analytics?.data && 
                   (facebookData.analytics.data.reach > 0 || facebookData.analytics.data.impressions > 0 || 
                    facebookData.analytics.data.engagement > 0 || facebookData.analytics.data.followers > 0) && (
                    <FacebookTiles 
                      data={facebookData.analytics.data}
                      onTileClick={handleTileClick}
                    />
                  )}
          
          <DetailedAnalytics 
            data={data} 
            formatNumber={formatNumber}
            onTimeRangeChange={handleTimeRangeChange}
            timeRange={timeRange}
            errors={apiData?.errors}
          />
          
          {/* Event Tiles Section */}
          <EventTiles />
        </div>
      </main>
      <Footer />
      <AIChatPrompt 
        isOpen={isAIChatPromptOpen} 
        onClose={() => setIsAIChatPromptOpen(false)} 
      />
      <FacebookAIAnalysis 
        tileData={selectedTile}
        isOpen={isAIAnalysisOpen}
        onClose={() => {
          setIsAIAnalysisOpen(false);
          setSelectedTile(null);
        }}
      />
      <FloatingAI onOpenAIChatPrompt={() => setIsAIChatPromptOpen(true)} />
    </div>
  );
};

const Dashboard = ({ user, onLogout }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <DashboardContent user={user} onLogout={onLogout} />
      </ErrorBoundary>
    </QueryClientProvider>
  );
};

export default Dashboard;
